import React, { useState, useEffect } from 'react';
import { Settings, Save, Shield, ShieldCheck, Globe, Moon } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const SettingsPanel = () => {
  const [settings, setSettings] = useState({
    coin_rate: '100',
    default_language: 'en',
    dark_mode: 'true',
    razorpay_key_id: '',
    razorpay_key_secret: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSetting = async (key, value) => {
    setSaving(true);
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, { key, value }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setSettings(prev => ({ ...prev, [key]: value }));
      }
    } catch (err) {
      alert(`Failed to save ${key}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-3xl font-extrabold text-slate-800">Global Settings Control</h2>
        <p className="text-slate-500 mt-1">Configure global exchange rates, default language profiles, and secure payment credentials.</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-20 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Retrieving registry variables...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* General Platform Config */}
          <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 space-y-6">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 pb-3 border-b border-slate-50">
              <Globe size={20} className="text-pink-600" /> Platform & Localization
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Default App Language</label>
                <select
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold"
                  value={settings.default_language}
                  onChange={(e) => handleSaveSetting('default_language', e.target.value)}
                >
                  <option value="en">English (US)</option>
                  <option value="hi">Hindi ( IN )</option>
                  <option value="ar">Arabic (AE)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Default Appearance Theme</label>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => handleSaveSetting('dark_mode', 'true')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold border transition ${settings.dark_mode === 'true' ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                  >
                    <Moon size={16} /> Dark Mode Default
                  </button>
                  <button
                    onClick={() => handleSaveSetting('dark_mode', 'false')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold border transition ${settings.dark_mode === 'false' ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                  >
                    Light Mode
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Secure Payment Gateway Config */}
          <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 space-y-6">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 pb-3 border-b border-slate-50">
              <ShieldCheck size={20} className="text-indigo-600" /> Razorpay Integration (Test Mode)
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Razorpay Key ID</label>
                <input
                  type="text"
                  placeholder="rzp_test_..."
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold"
                  value={settings.razorpay_key_id}
                  onChange={(e) => setSettings({ ...settings, razorpay_key_id: e.target.value })}
                  onBlur={() => handleSaveSetting('razorpay_key_id', settings.razorpay_key_id)}
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase">Razorpay Key Secret</label>
                <input
                  type="password"
                  placeholder="••••••••••••••"
                  className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 font-semibold"
                  value={settings.razorpay_key_secret}
                  onChange={(e) => setSettings({ ...settings, razorpay_key_secret: e.target.value })}
                  onBlur={() => handleSaveSetting('razorpay_key_secret', settings.razorpay_key_secret)}
                />
              </div>
              <p className="text-[10px] text-slate-400 italic">Settings are auto-saved on field blur (focus exit).</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
